import axios from "../axios";
import hostConfig from "@/config/hostResolver";

const baseUrl = process.env.VUE_APP_PROGRAM_URL ?? `${hostConfig.apiBaseUrl}/program-service`;

class ProgramService {
  // ############ Programs ############
  uploadProgramImage(programId, attachment, clientId, imageAltText) {
    let formData = new FormData();

    formData.append("file", attachment);
    formData.append("clientId", clientId);
    if (imageAltText != null) formData.append("imageAltText", imageAltText);
    return axios
      .post(baseUrl + "/programs/image/" + programId, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        console.log(response);
        return response.data;
      })
      .catch((error) => {
        console.log("Error!", error);
        return error;
      });
  }
  duplicateProgramImage(oldId, newId, oldFilePath, clientId, imageAltText) {
    var body = {
      newProgramId: newId,
      clientId: clientId,
      imageAltText: imageAltText,
    };

    // If we're duplicating an old program, we use oldProgramId
    // If we're duplicating an image from a learning module, we send the content filepath
    if (oldId) {
      body.oldProgramId = oldId;
    } else if (oldFilePath) {
      body.oldFilePath = oldFilePath;
    }
    return axios
      .post(baseUrl + "/programs/image/duplicate", body)
      .then((response) => {
        console.log(response);
        return response.data;
      })
      .catch((error) => {
        console.log("Error!", error);
        return error;
      });
  }
  deleteProgramImage(programId, clientId) {
    return axios
      .delete(baseUrl + "/programs/image/" + clientId + "/" + programId)
      .then((response) => {
        console.log(response.data);
        return response.data;
      })
      .catch((error) => {
        console.log("Error!", error);
        return error;
      });
  }
  getProgramLeaderboard(programId) {
    return axios
      .get(baseUrl + "/programs/leaderboard/" + programId)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        console.log("Error!", error.response);
        return error;
      });
  }
  getLeaderboardV3(programId) {
    // VUE_APP_V3_SERVICE_URL is not wired up in any deployed environment's build
    // (missing from cloudbuild.yaml's create-env allowlist), so this always falls
    // through to the fallback. `baseUrl` is scoped to the v1 "/program-service" LB
    // prefix rewrite, which routes to the old Express service - it has no v3 routes
    // and returns a bare 404. The v3 (tsoa) service is only reachable through the
    // separate "/program-service-ts" LB prefix rewrite, so the fallback must target
    // that prefix instead of reusing baseUrl.
    const v3Url =
      process.env.VUE_APP_V3_SERVICE_URL || `${hostConfig.apiBaseUrl}/program-service-ts`;
    return axios.get(v3Url + "/v3/program/" + programId + "/leaderboard");
  }
  getTabsV3(programId, clientId) {
    const v3Url =
      process.env.VUE_APP_V3_SERVICE_URL || `${hostConfig.apiBaseUrl}/program-service-ts`;
    return axios
      .get(v3Url + "/v3/program-detail-tab/program/" + programId, { params: { clientId } })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        console.log("Error!", error);
        throw error;
      });
  }
  upsertTabsV3(programId, tabs, clientId) {
    const v3Url =
      process.env.VUE_APP_V3_SERVICE_URL || `${hostConfig.apiBaseUrl}/program-service-ts`;
    return axios
      .put(
        v3Url + "/v3/program-detail-tab/program/" + programId,
        { tabs },
        { params: { clientId } }
      )
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        console.log("Error!", error);
        throw error;
      });
  }
  deleteProgram(programId) {
    return axios
      .delete(baseUrl + "/programs/" + programId)
      .then((response) => {
        return response.data;
      })
      .catch((e) => {
        throw e && e.response ? e.response.data || e.response : e;
      });
  }

  // ############ Metrics ############
  getMetrics(queryString = "") {
    return axios
      .get(baseUrl + "/metrics" + queryString)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        console.log("Error!", error.response);
        return error;
      });
  }
  createMetric(metric) {
    return axios
      .post(baseUrl + "/metrics", metric)
      .then((response) => {
        console.log(response);
        return response.data;
      })
      .catch((error) => {
        console.log("Error!", error);
        return error;
      });
  }
  updateMetric(metricId, metric) {
    return axios
      .patch(baseUrl + "/metrics/" + metricId, metric)
      .then((response) => {
        console.log(response);
        return response.data;
      })
      .catch((error) => {
        console.log("Error!", error);
        return error;
      });
  }

  deleteMetric(metricId) {
    return axios
      .delete(baseUrl + "/metrics/" + metricId)
      .then((response) => {
        console.log(response);
        return response.data;
      })
      .catch((error) => {
        console.log("Error!", error);
        return error;
      });
  }

  // ############ ProgramMetrics ############
  // Takes an array of user ids (users) and an array of metric ids (metrics)
  getLatestMetricValues(body) {
    return axios
      .post(baseUrl + "/programMetrics/recent", body)
      .then((response) => {
        console.log(response);
        return response.data;
      })
      .catch((error) => {
        console.log("Error!", error.response);
        return error;
      });
  }
  uploadMetricCSV(file, programId, timezone, clientId) {
    let formData = new FormData();
    formData.append("file", file);
    formData.append("timezone", timezone);
    formData.append("clientId", clientId);
    return axios
      .post(baseUrl + "/programMetrics/csv/" + programId, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  }
  downloadMetricTemplateFile(metricId) {
    return axios.get(baseUrl + "/programMetrics/template/" + metricId).then((response) => {
      console.log("Downloading template file");
      let blob = new Blob([response.data], { type: "text/csv" });
      let link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute("download", "Program_Metric_Template.csv");
      document.body.appendChild(link);
      link.click();
    });
  }
  postProgramMetricBulk(body) {
    return axios
      .post(baseUrl + "/programMetrics/bulk", body)
      .then((response) => {
        console.log(response);
        return response;
      })
      .catch((error) => {
        console.log(error);
        return error;
      });
  }
  postSelfAssessMetric(metricId, body, programId = null) {
    const q = programId ? `?programId=${programId}` : "";
    return axios
      .post(`${baseUrl}/programMetrics/selfAssess/${metricId}${q}`, body)
      .then((response) => {
        console.log(response);
        return response;
      })
      .catch((error) => {
        console.log(error);
        throw error;
      });
  }

  // ############ Program Attachments ############
  createProgramAttachment(body) {
    return axios
      .post(baseUrl + "/programs/attachments", body)
      .then((response) => {
        console.log(response);
        return response.data.programAttachment;
      })
      .catch((error) => {
        console.log("Error!", error);
        return error;
      });
  }

  // V2
  getProgramsV2(params) {
    return axios
      .get(baseUrl + "/v2/program", { params })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        console.log("Error getting programs for admin page ", error.response);
        throw error.response;
      });
  } // end getProgramsV2

  getProgramsAdminV2(context, clientId) {
    return axios
      .get(`${baseUrl}/v2/program/admin?clientId=${clientId}&context=${context}`)
      .then((response) => {
        return response.data.result;
      })
      .catch((error) => {
        console.log("Error getting program (v2)", error.response);
        throw error;
      });
  } // end getProgramsAdminV2

  getProgramForWizardV2(programId, clientId, context) {
    return axios
      .get(`${baseUrl}/v2/program/admin/${programId}?clientId=${clientId}&context=${context}`)
      .then((response) => {
        return response.data.result;
      })
      .catch((error) => {
        console.log("Error getting program for wizard", error.response);
        throw error;
      });
  } // end getProgramForWizardV2

  getProgramsByUserV2(programId, includeSurveys = true, includeArchived = false) {
    var endpointString = "/v2/program/user";
    if (programId !== null) endpointString += "/" + programId;
    return axios
      .get(
        baseUrl +
          endpointString +
          "?surveys=" +
          includeSurveys +
          "&includeArchived=" +
          includeArchived
      )
      .then((response) => {
        return response.data.result;
      })
      .catch((error) => {
        console.log("Error getting programs for user ", error.response);
        throw error;
      });
  } // end getProgramsByUserV2

  getProgramDashboardV2(params) {
    return axios
      .get(baseUrl + "/v2/program/user/dashboard", { params })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        console.log("Error getting program dashboard rows ", error.response);
        throw error.response;
      });
  } // end getProgramDashboardV2

  getUserSummary() {
    return axios
      .get(baseUrl + "/v2/program/user/summary")
      .then((response) => {
        return response.data.result;
      })
      .catch((error) => {
        console.log("Error getting user summary ", error.response);
        throw error;
      });
  } // end getUserSummary

  getMetricsSummary() {
    return axios
      .get(baseUrl + "/v2/program/user/metrics-summary")
      .then((response) => {
        return response.data.result;
      })
      .catch((error) => {
        console.log("Error getting metrics summary ", error.response);
        throw error;
      });
  } // end getMetricsSummary

  getProgressForProgramElement(elementId) {
    return axios
      .get(`${baseUrl}/v2/program-element/search/progress/${elementId}`)
      .then((response) => {
        return response.data.result.rows;
      })
      .catch((error) => {
        console.log("Error getting program element progress", error.response);
        throw error;
      });
  } // end getProgressForProgramElement

  createProgramV2(body) {
    return axios
      .post(baseUrl + "/v2/program", body)
      .then((response) => {
        return response.data.result;
      })
      .catch((error) => {
        console.log("Error creating program ", error.response);
        throw error.response;
      });
  } // end createProgramV2

  updateProgramV2(id, body) {
    return axios
      .patch(`${baseUrl}/v2/program/${id}`, body)
      .then((response) => {
        return response.data.result;
      })
      .catch((error) => {
        console.log("Error updating program ", error.response);
        throw error.response;
      });
  } // end updateProgramV2

  deleteMetricV2(id) {
    return axios
      .delete(`${baseUrl}/v2/metric-definition/${id}`)
      .then((response) => {
        return response.data.result;
      })
      .catch((e) => {
        console.log("Error deleting metric ", e.response);
        throw e && e.response ? e.response.data || e.response : e;
      });
  } // end updateProgramV2

  async getProcoreCompanies(opt = {}) {
    return await axios.get(`${baseUrl}/v2/procore/companies`, opt);
  } // end getProcoreCompanies

  async getProcoreProjects(opt = {}) {
    return await axios.get(`${baseUrl}/v2/procore/projects`, opt);
  } // end getProcoreProjects

  async getProcorePrograms(opt = {}) {
    return await axios.get(`${baseUrl}/v2/procore/programs`, opt);
  } // end getProcorePrograms

  async getProcoreConfigurableFields(projectId, opt = {}) {
    return await axios.get(`${baseUrl}/v2/procore/projects/${projectId}/fields`, opt);
  } // end getProcoreConfigurableFields

  async getProcorePeopleInProject(projectId, opt = {}) {
    return await axios.get(`${baseUrl}/v2/procore/projects/${projectId}/people`, opt);
  } // end getProcorePeopleInProject

  async procoreCompleteRegistration(body, opt = {}) {
    return await axios.post(`${baseUrl}/v2/procore/complete-registration`, body, opt);
  } // end procoreCompleteRegistration

  async procoreUpdateRegistration(programId, editType, body, opt = {}) {
    return await axios.patch(
      `${baseUrl}/v2/procore/complete-registration/${programId}/${editType}`,
      body,
      opt
    );
  } // end procoreUpdateRegistration

  async getDashboardRowTypes(opt = {}) {
    return await axios.get(`${baseUrl}/v2/dashboard-row-type`, opt);
  } // end getDashboardRowTypes

  async getDashboardWidgetTypes(opt = {}) {
    return await axios.get(`${baseUrl}/v2/dashboard-widget-type`, opt);
  } // end getDashboardWidgetTypes

  async createDashboardRow(body, opt = {}) {
    return await axios.post(`${baseUrl}/v2/dashboard-row`, body, opt);
  } // end createDashboardRow

  async updateDashboardRow(rowId, body, opt = {}) {
    return await axios.patch(`${baseUrl}/v2/dashboard-row/${rowId}`, body, opt);
  } // end updateDashboardRow

  async updateDashboardRowOrder(body, opt = {}) {
    return await axios.patch(`${baseUrl}/v2/dashboard-row/reorder`, body, opt);
  } // end updateDashboardRowOrder

  async deleteDashboardRow(rowId, opt = {}) {
    return await axios.delete(`${baseUrl}/v2/dashboard-row/${rowId}`, opt);
  } // end deleteDashboardRow

  async createDashboardColumn(body, opt = {}) {
    return await axios.post(`${baseUrl}/v2/dashboard-column`, body, opt);
  } // end createDashboardColumn

  async updateDashboardColumn(columnId, body, opt = {}) {
    return await axios.patch(`${baseUrl}/v2/dashboard-column/${columnId}`, body, opt);
  } // end updateDashboardColumn

  async deleteDashboardColumn(columnId, opt = {}) {
    return await axios.delete(`${baseUrl}/v2/dashboard-column/${columnId}`, opt);
  } // end updateDashboardColumn

  async uploadDashboardColumnImage(columnId, attachment) {
    let formData = new FormData();

    formData.append("file", attachment);
    return await axios.post(baseUrl + "/v2/dashboard-column/" + columnId + "/image", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  } // end uploadDashboardColumnImage

  async getClientDashboardDefaults(clientId, opt = {}) {
    return await axios.get(`${baseUrl}/v2/client-dashboard-defaults/${clientId}`, opt);
  } // end getClientDashboardDefaults

  async updateClientDashboardDefaults(clientId, body, opt = {}) {
    return await axios.patch(`${baseUrl}/v2/client-dashboard-defaults/${clientId}`, body, opt);
  } // end updateClientDashboardDefaults

  async getProgramDefaults(clientId, opt = {}) {
    if (!clientId && clientId !== 0) throw new Error("clientId is required");
    return await axios.get(
      `${baseUrl}/v2/procore/program-defaults?filter=${encodeURIComponent(
        `clientId = ${clientId}`
      )}`,
      opt
    );
  } // end getProgramDefaults

  async saveProgramDefaults(body, opt = {}) {
    return await axios.put(`${baseUrl}/v2/procore/program-defaults`, body, opt);
  } // end saveProgramDefaults

  async getProgramDashboardRows(params = {}, opt = {}) {
    return await axios.get(`${baseUrl}/v2/dashboard/program-rows`, { params, ...opt });
  } // end getProgramDashboardRows
}

export default new ProgramService();
